"use client";

import { useEffect, useRef, useState } from "react";
import { addVouch, type Vouch } from "@/app/actions/vouches";

export function VouchModal({
  candidateName,
  voucheeId,
  onClose,
  onVouched,
}: {
  candidateName: string;
  voucheeId: string;
  onClose: () => void;
  onVouched: (vouch: Vouch) => void;
}) {
  const [relationship, setRelationship] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  async function handleSubmit() {
    if (!relationship.trim()) return;
    setSubmitting(true);
    setError(null);
    const result = await addVouch(voucheeId, relationship.trim(), message.trim() || undefined);
    if (result.error) {
      setError(result.error);
      setSubmitting(false);
    } else {
      onVouched({
        id: crypto.randomUUID(),
        voucher_id: "",
        voucher_first_name: "You",
        voucher_last_name: "",
        voucher_photo_url: null,
        voucher_profile_id: "",
        relationship: relationship.trim(),
        message: message.trim() || null,
        created_at: new Date().toISOString(),
      });
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vouch-modal-title"
        className="relative z-10 bg-white rounded-[16px] p-5 sm:p-7 md:p-9 w-full max-w-[480px] shadow-[0_24px_80px_rgba(0,0,0,0.15)] animate-[scaleIn_0.25s_ease-out]"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
        >
          &times;
        </button>
        <h3 id="vouch-modal-title" className="font-serif text-xl mb-1">
          Vouch for {candidateName}
        </h3>
        <p className="text-[13px] text-text-muted mb-5">
          Let employers know you&apos;ve worked with {candidateName} and can recommend them.
        </p>

        <label htmlFor="vouch-relationship" className="block text-[12px] font-semibold text-charcoal mb-1.5">
          How do you know each other? <span className="text-error">*</span>
        </label>
        <input
          id="vouch-relationship"
          ref={inputRef}
          type="text"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          placeholder="e.g. Worked together at Val d'Isere 2024/25"
          className="w-full p-3 border-[1.5px] border-border rounded-md text-sm font-sans bg-warm-bg outline-none focus:border-gold mb-4"
        />

        <label htmlFor="vouch-message" className="block text-[12px] font-semibold text-charcoal mb-1.5">
          Short message <span className="text-text-muted font-normal">(optional, max 200 chars)</span>
        </label>
        <textarea
          id="vouch-message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 200))}
          placeholder="e.g. Hardest worker on the mountain, always on time"
          rows={3}
          className="w-full p-3 border-[1.5px] border-border rounded-md text-sm font-sans bg-warm-bg outline-none focus:border-gold resize-none"
        />
        <p className="text-[11px] text-text-muted text-right mt-1 mb-3">
          {message.length}/200
        </p>

        {error && (
          <p className="text-xs text-error mb-3">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 px-5 py-2.5 text-[13px] font-semibold text-text-muted bg-transparent border-[1.5px] border-border rounded-sm cursor-pointer font-sans hover:border-charcoal"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!relationship.trim() || submitting}
            className="min-h-11 px-5 py-2.5 text-[13px] font-semibold text-white bg-gold rounded-sm border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Vouch"}
          </button>
        </div>
      </div>
    </div>
  );
}
